/**
 * The background work that moves photos separately from the game once a send has timed out — see
 * `photoDelivery.ts` for the why and the bookkeeping. Three small jobs, each a visible-screen poller
 * (`livePoll.ts`): quiet when the phone is idle, backing off on errors, snapping back on a tap.
 *
 *  - Host: upload the photos the guests have not been given yet (one round per request).
 *  - Host: pick up photos guests promised, and put each on the round it belongs to.
 *  - Guest: send the phone's own photo for a round that went without it.
 *
 * Nothing here is essential to scoring: a round is complete without its photo, and every job just
 * keeps trying until it gets through, the link is gone, or nobody could use the photo any more.
 */

import type { Game } from '../game/types';
import {
  fetchRoundPhotos,
  fetchSubmissionPhotos,
  uploadRoundPhotos,
  uploadSubmissionPhotos,
  type ShareResult,
} from './gameShare';
import { startPoller, type Poller } from './livePoll';
import {
  GUEST_LINK_WINDOW_MS,
  attachPhotoToRound,
  clearGuestPhotoJobs,
  loadAwaitedPhotos,
  loadGuestPhotoJobs,
  markConnectionFine,
  markRoundPhotosUploaded,
  removeAwaitedPhoto,
  removeGuestPhotoJob,
  roundsOwingPhotos,
  type FetchedRoundPhotos,
} from './photoDelivery';

/** A refusal that will not change on retry: the link is gone (or the photo unacceptable), so stop asking. */
function isFinal(result: Extract<ShareResult<unknown>, { ok: false }>): boolean {
  return result.status === 401 || result.status === 403 || result.status === 404 || result.status === 400;
}

/** A little slack past the guests' hour, so a photo that arrives right at the edge is still picked up. */
const HOST_GIVE_UP_AFTER_MS = GUEST_LINK_WINDOW_MS + 5 * 60 * 1000;

/** True once nobody could use this game's photos any more: its guests' link has expired. */
export function guestWindowClosed(game: Pick<Game, 'endedAt'>, now: number = Date.now()): boolean {
  return game.endedAt !== undefined && now - game.endedAt > HOST_GIVE_UP_AFTER_MS;
}

// ---------------------------------------------------------------------------
// Host -> guests: the photos the guests have not been given yet
// ---------------------------------------------------------------------------

let uploader: Poller | undefined;
let uploaderGame: Game | undefined;

/**
 * Starts (or nudges) the upload of every photo the guests are still owed for this game. Safe to call
 * whenever a round is recorded or a screen opens: it does nothing when nothing is owed, and does not
 * start a second worker for a game that already has one.
 */
export function ensureRoundPhotoUploads(game: Game, ownerToken: string): void {
  if (uploader?.isRunning() && uploaderGame === game) {
    uploader.pokeNow();
    return;
  }
  uploader?.stop();
  if (roundsOwingPhotos(game.id, game.rounds).length === 0) return;
  uploaderGame = game;
  uploader = startPoller({
    baseMs: 5_000,
    minGapMs: 3_000,
    tick: async () => {
      if (guestWindowClosed(game)) return 'stop';
      const owed = roundsOwingPhotos(game.id, game.rounds);
      for (const round of owed) {
        const result = await uploadRoundPhotos(ownerToken, round.index, round.recordedAt, round.photos ?? []);
        if (result.ok) {
          markRoundPhotosUploaded(game.id, round);
          continue;
        }
        if (!isFinal(result)) return 'error';
        if (result.status === 400) {
          // This round's photos can never be accepted; do not let them block the rest.
          markRoundPhotosUploaded(game.id, round);
          continue;
        }
        return 'stop'; // link gone
      }
      return roundsOwingPhotos(game.id, game.rounds).length === 0 ? 'stop' : 'changed';
    },
  });
}

export function stopRoundPhotoUploads(): void {
  uploader?.stop();
  uploader = undefined;
  uploaderGame = undefined;
}

// ---------------------------------------------------------------------------
// Guest -> host: photos guests promised, waiting to be attached
// ---------------------------------------------------------------------------

let pickup: Poller | undefined;
let pickupGame: Game | undefined;

/**
 * Starts (or nudges) the pick-up of photos guests said would follow their round. When one arrives
 * it goes onto the round it belongs to; `onAttached` then saves the game and shares it with the guests.
 */
export function ensureAwaitedPhotoPickup(game: Game, ownerToken: string, onAttached: () => Promise<void> | void): void {
  if (pickup?.isRunning() && pickupGame === game) {
    pickup.pokeNow();
    return;
  }
  pickup?.stop();
  if (loadAwaitedPhotos(game.id).length === 0) return;
  pickupGame = game;
  pickup = startPoller({
    baseMs: 10_000,
    minGapMs: 5_000,
    tick: async () => {
      const awaited = loadAwaitedPhotos(game.id);
      if (awaited.length === 0) return 'stop';
      if (guestWindowClosed(game)) {
        for (const a of awaited) removeAwaitedPhoto(game.id, a.submissionId);
        return 'stop';
      }
      const result = await fetchSubmissionPhotos(ownerToken, awaited.map((a) => a.submissionId));
      if (!result.ok) return isFinal(result) ? 'stop' : 'error';
      let attached = false;
      for (const a of awaited) {
        const photos = result.data[a.submissionId];
        if (!photos) continue; // not sent yet
        if (attachPhotoToRound(game.rounds, a, photos)) attached = true;
        removeAwaitedPhoto(game.id, a.submissionId); // attached, or the round is gone/has its own photo: done either way
      }
      if (attached) await onAttached();
      return loadAwaitedPhotos(game.id).length === 0 ? 'stop' : attached ? 'changed' : 'unchanged';
    },
  });
}

// ---------------------------------------------------------------------------
// Guest: send this phone's own photo after its round
// ---------------------------------------------------------------------------

let guestUploader: Poller | undefined;

export type GuestPhotoOutcome = 'sent' | 'expired' | 'refused';

/**
 * Sends the photos this phone held back from its rounds, oldest first, retrying until they get through. `onDone`
 * says how each one ended so the screen can tell the guest: `sent`, `expired` (the link is gone — too late, and every
 * photo still waiting is dropped with it), or `refused` (the relay would not take that one; the rest carry on).
 * A photo getting through is also the sign the connection is fine again, so the phone stops treating it as slow.
 * `immediate` starts at once instead of after the usual short delay — right after a round has just gone through.
 */
export function ensureGuestPhotoUpload(
  shareToken: string,
  gameId: string,
  onDone: (outcome: GuestPhotoOutcome) => void,
  immediate = false,
): void {
  if (guestUploader?.isRunning()) {
    guestUploader.pokeNow();
    return;
  }
  if (loadGuestPhotoJobs(gameId).length === 0) return;
  guestUploader = startPoller({
    baseMs: 5_000,
    minGapMs: 3_000,
    tick: async () => {
      for (const job of loadGuestPhotoJobs(gameId)) {
        const result = await uploadSubmissionPhotos(shareToken, job.submissionId, job.photos);
        if (result.ok) {
          removeGuestPhotoJob(gameId, job.submissionId);
          markConnectionFine(gameId);
          onDone('sent');
          continue;
        }
        if (!isFinal(result)) return 'error';
        if (result.status === 400) {
          removeGuestPhotoJob(gameId, job.submissionId);
          onDone('refused');
          continue;
        }
        clearGuestPhotoJobs(gameId);
        onDone('expired');
        return 'stop';
      }
      return loadGuestPhotoJobs(gameId).length === 0 ? 'stop' : 'changed';
    },
  });
  if (immediate) guestUploader.pokeNow();
}

// ---------------------------------------------------------------------------
// Guest: fetch the host's held-back photos
// ---------------------------------------------------------------------------

/** What each guest link has loaded so far this session, by game then round. */
const loadedByGame = new Map<string, Map<number, FetchedRoundPhotos>>();

export function loadedPhotosFor(gameId: string): ReadonlyMap<number, FetchedRoundPhotos> {
  return loadedByGame.get(gameId) ?? new Map();
}

export interface GetPhotosResult {
  /** Rounds asked for. */
  asked: number;
  /** Rounds whose photos arrived now. */
  loaded: number;
  /** The connection failed part-way (timeout or offline) — try again. */
  failed: boolean;
  /** The link is gone (an hour after the game ended) — no point trying again. */
  expired: boolean;
}

/**
 * Asks for the photos of the rounds the host held back. Fetched in small groups so one slow reply
 * cannot sink everything: whatever arrived is kept even when a later group fails.
 */
export async function getHeldPhotos(shareToken: string, gameId: string, wantedRounds: number[]): Promise<GetPhotosResult> {
  const have = loadedByGame.get(gameId) ?? new Map<number, FetchedRoundPhotos>();
  loadedByGame.set(gameId, have);
  let loaded = 0;
  const GROUP = 3;
  for (let i = 0; i < wantedRounds.length; i += GROUP) {
    const group = wantedRounds.slice(i, i + GROUP);
    const result = await fetchRoundPhotos(shareToken, group);
    if (!result.ok) {
      return { asked: wantedRounds.length, loaded, failed: !isFinal(result), expired: isFinal(result) };
    }
    for (const [key, value] of Object.entries(result.data)) {
      have.set(Number(key), value);
      loaded++;
    }
  }
  return { asked: wantedRounds.length, loaded, failed: false, expired: false };
}
